import { useSyncExternalStore } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { Feriado, TipoFeriado } from './feriados';
import { idioma } from './i18n';

// Feriados de outros países: base pública e gratuita Nager.Date (https://date.nager.at), mantida em
// código aberto e usada por muitos sistemas. Não precisa de chave e não recebe nenhum dado pessoal:
// o app só pede "feriados de 2027 dos Estados Unidos", por exemplo.
// Cada país e ano baixado fica guardado no aparelho e é renovado a cada 30 dias.

const API = 'https://date.nager.at/api/v3';
const VALIDADE = 30 * 24 * 60 * 60 * 1000;

/** Países disponíveis (código ISO e nome em português). O Brasil é calculado no próprio app. */
export const PAISES_MUNDO: [string, string][] = [
  ['ZA', 'África do Sul'], ['AL', 'Albânia'], ['DE', 'Alemanha'], ['AD', 'Andorra'], ['AR', 'Argentina'], ['AM', 'Armênia'],
  ['AU', 'Austrália'], ['AT', 'Áustria'], ['BS', 'Bahamas'], ['BB', 'Barbados'], ['BE', 'Bélgica'], ['BZ', 'Belize'], ['BJ', 'Benin'],
  ['BY', 'Bielorrússia'], ['BO', 'Bolívia'], ['BA', 'Bósnia e Herzegovina'], ['BW', 'Botsuana'], ['BR', 'Brasil'], ['BG', 'Bulgária'],
  ['CA', 'Canadá'], ['KZ', 'Cazaquistão'], ['CL', 'Chile'], ['CN', 'China'], ['CY', 'Chipre'], ['CO', 'Colômbia'], ['KR', 'Coreia do Sul'],
  ['CR', 'Costa Rica'], ['HR', 'Croácia'], ['CU', 'Cuba'], ['DK', 'Dinamarca'], ['EG', 'Egito'], ['SV', 'El Salvador'], ['EC', 'Equador'],
  ['SK', 'Eslováquia'], ['SI', 'Eslovênia'], ['ES', 'Espanha'], ['US', 'Estados Unidos'], ['EE', 'Estônia'], ['FI', 'Finlândia'],
  ['FR', 'França'], ['GA', 'Gabão'], ['GM', 'Gâmbia'], ['GE', 'Geórgia'], ['GI', 'Gibraltar'], ['GD', 'Granada'], ['GR', 'Grécia'],
  ['GL', 'Groenlândia'], ['GT', 'Guatemala'], ['GY', 'Guiana'], ['HT', 'Haiti'], ['NL', 'Holanda (Países Baixos)'], ['HN', 'Honduras'],
  ['HK', 'Hong Kong'], ['HU', 'Hungria'], ['ID', 'Indonésia'], ['IE', 'Irlanda'], ['IS', 'Islândia'], ['IT', 'Itália'], ['JM', 'Jamaica'],
  ['JP', 'Japão'], ['LS', 'Lesoto'], ['LV', 'Letônia'], ['LI', 'Liechtenstein'], ['LT', 'Lituânia'], ['LU', 'Luxemburgo'],
  ['MK', 'Macedônia do Norte'], ['MG', 'Madagascar'], ['MT', 'Malta'], ['MA', 'Marrocos'], ['MX', 'México'], ['MZ', 'Moçambique'],
  ['MD', 'Moldávia'], ['MC', 'Mônaco'], ['MN', 'Mongólia'], ['ME', 'Montenegro'], ['NA', 'Namíbia'], ['NI', 'Nicarágua'], ['NE', 'Níger'],
  ['NG', 'Nigéria'], ['NO', 'Noruega'], ['NZ', 'Nova Zelândia'], ['PA', 'Panamá'], ['PG', 'Papua-Nova Guiné'], ['PY', 'Paraguai'],
  ['PE', 'Peru'], ['PL', 'Polônia'], ['PR', 'Porto Rico'], ['PT', 'Portugal'], ['KE', 'Quênia'], ['GB', 'Reino Unido'],
  ['CZ', 'República Tcheca'], ['DO', 'República Dominicana'], ['RO', 'Romênia'], ['RU', 'Rússia'], ['SM', 'San Marino'],
  ['RS', 'Sérvia'], ['SG', 'Singapura'], ['SE', 'Suécia'], ['CH', 'Suíça'], ['SR', 'Suriname'], ['TN', 'Tunísia'], ['TR', 'Turquia'],
  ['UA', 'Ucrânia'], ['UY', 'Uruguai'], ['VA', 'Vaticano'], ['VE', 'Venezuela'], ['VN', 'Vietnã'], ['ZW', 'Zimbábue'],
];

/** Nome do país no idioma do app (quando o aparelho sabe traduzir), senão em português. */
export function nomePais(cod: string) {
  const pt = PAISES_MUNDO.find(p => p[0] === cod)?.[1] ?? cod;
  if (idioma() === 'pt-BR') return pt;
  try {
    const DN = (Intl as any).DisplayNames;
    if (DN) return new DN([idioma()], { type: 'region' }).of(cod) || pt;
  } catch { /* sem tradução no aparelho */ }
  return pt;
}

type Bruto = { date: string; localName: string; name: string; global: boolean; counties: string[] | null; types?: string[] };
type Guardado = { em: number; itens: Bruto[] };

const memoria = new Map<string, Guardado | 'erro' | 'sem-dados'>();
const pedindo = new Set<string>();
let versao = 0;
const ouvintes = new Set<() => void>();
const avisar = () => { versao++; ouvintes.forEach(f => f()); };
/** Muda sempre que chegam feriados novos de algum país (as telas usam para recalcular). */
export const useVersaoFeriados = () => useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => versao);

const k = (pais: string, ano: number) => `mp_fer_${pais}_${ano}`;

async function carregar(pais: string, ano: number, forcar = false) {
  const id = k(pais, ano);
  if (pedindo.has(id)) return;
  pedindo.add(id);
  try {
    if (!forcar && !memoria.has(id)) {
      const s = await AsyncStorage.getItem(id).catch(() => null);
      if (s) {
        const g = JSON.parse(s) as Guardado;
        memoria.set(id, g); avisar();
        if (Date.now() - g.em < VALIDADE) return;
      }
    }
    const r = await fetch(`${API}/PublicHolidays/${ano}/${pais}`);
    if (r.status === 204 || r.status === 404) { if (!memoria.has(id)) { memoria.set(id, 'sem-dados'); avisar(); } return; }
    if (!r.ok) throw new Error(String(r.status));
    const itens = ((await r.json()) as Bruto[]).filter(x => x && /^\d{4}-\d{2}-\d{2}$/.test(x.date));
    const g: Guardado = { em: Date.now(), itens };
    memoria.set(id, g);
    AsyncStorage.setItem(id, JSON.stringify(g)).catch(() => {});
    avisar();
  } catch {
    if (!memoria.has(id)) { memoria.set(id, 'erro'); avisar(); }
  } finally { pedindo.delete(id); }
}

/** Situação dos dados de um país num ano (para mostrar "carregando" ou "sem internet"). */
export function situacao(pais: string, ano: number): 'ok' | 'carregando' | 'erro' | 'sem-dados' {
  const m = memoria.get(k(pais, ano));
  if (!m) { carregar(pais, ano); return 'carregando'; }
  return typeof m === 'string' ? m : 'ok';
}
/** Tenta baixar de novo (botão "Tentar de novo"). */
export function recarregar(pais: string, ano: number) { memoria.delete(k(pais, ano)); carregar(pais, ano, true); }

function tipoDe(b: Bruto): TipoFeriado | null {
  const tipos = b.types ?? ['Public'];
  if (tipos.includes('Public')) return b.global || !b.counties?.length ? 'nacional' : 'regional';
  if (tipos.includes('Optional') || tipos.includes('Observance')) return 'facultativo';
  return null; // só bancário, escolar ou de repartições: fica de fora
}

/** Feriados de um país num ano: os nacionais e os das regiões escolhidas. Sem dados ainda, devolve [] e começa a baixar. */
export function feriadosExternos(pais: string, ano: number, regioes: string[]): Feriado[] {
  const m = memoria.get(k(pais, ano));
  if (!m) { carregar(pais, ano); return []; }
  if (typeof m === 'string') return [];
  const out: Feriado[] = [];
  for (const b of m.itens) {
    const tipo = tipoDe(b);
    if (!tipo) continue;
    const regs = b.counties ?? [];
    if (regs.length && !b.global) {
      const minhas = regs.filter(r => regioes.includes(r));
      if (!minhas.length) continue;
      out.push({ data: b.date, nome: b.localName || b.name, tipo: tipo === 'facultativo' ? 'facultativo' : 'regional', local: minhas.map(nomeRegiao).join(', '), pais });
    } else out.push({ data: b.date, nome: b.localName || b.name, tipo, local: nomePais(pais), pais });
  }
  return out;
}

/** Regiões (estados, províncias...) que aparecem nos feriados do país naquele ano. */
export function regioesDoPais(pais: string, ano: number): string[] {
  const m = memoria.get(k(pais, ano));
  if (!m || typeof m === 'string') return [];
  return [...new Set(m.itens.flatMap(b => b.counties ?? []))].sort();
}
/** "US-CA" vira "CA". */
export const nomeRegiao = (cod: string) => cod.includes('-') ? cod.split('-').slice(1).join('-') : cod;
